import React, { useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { FiCheckCircle, FiPackage, FiTruck, FiPhone, FiClock } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';

const ThankYou = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const orderData = location.state;

  useEffect(() => {
    if (!orderData) {
      navigate('/');
    }
  }, [orderData, navigate]);

  if (!orderData) return null;

  const { orderNumber, clientName, clientPhone, total, itemsCount, items, timestamp } = orderData;

  const handleWhatsAppSupport = () => {
    const message = `Bonjour, j'ai une question concernant ma commande #${orderNumber}`;
    window.open(`https://wa.me/212665078092?text=${encodeURIComponent(message)}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-neutral-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        
        {/* Success Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-black rounded-full mb-4">
            <FiCheckCircle className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-light tracking-wide text-neutral-900 mb-2">
            Merci pour votre commande
          </h1>
          <p className="text-lg text-neutral-600 font-light">
            {clientName}
          </p>
        </div>

        {/* Order Details Card */}
        <div className="bg-white border border-neutral-200 mb-6">
          <div className="p-6 border-b border-neutral-200">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <p className="text-sm text-neutral-600 font-light mb-1">Numéro de commande</p>
                <p className="text-2xl font-normal text-neutral-900">#{orderNumber}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-neutral-600 font-light mb-1">Date</p>
                <p className="text-sm text-neutral-900">{timestamp}</p>
              </div>
            </div>
          </div>

          {/* Status */}
          <div className="p-6 bg-neutral-50 border-b border-neutral-200">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <FiPackage className="w-6 h-6 text-neutral-900" />
              </div>
              <div>
                <h3 className="text-base font-normal text-neutral-900 mb-1">
                  Votre commande est en cours de traitement
                </h3>
                <p className="text-sm text-neutral-600 font-light">
                  Notre équipe prépare votre commande avec soin
                </p>
              </div>
            </div>
          </div>

          {/* Delivery Info */}
          <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-6 border-b border-neutral-200">
            <div className="flex items-start gap-3">
              <FiTruck className="w-5 h-5 text-neutral-900 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-normal text-neutral-900 mb-1">Livraison estimée</p>
                <p className="text-sm text-neutral-600 font-light">1-2 jours ouvrables</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <FiPhone className="w-5 h-5 text-neutral-900 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-normal text-neutral-900 mb-1">Contact</p>
                <p className="text-sm text-neutral-600 font-light">{clientPhone}</p>
              </div>
            </div>
          </div>

          {/* Order Items */}
          <div className="p-6">
            <h3 className="text-sm font-normal text-neutral-900 mb-4 uppercase tracking-wider">
              Articles commandés ({itemsCount})
            </h3>
            <div className="space-y-4">
              {items && items.map((item, index) => {
                const itemPrice = item.promotional_price || item.selling_price || 0;
                const itemQuantity = item.quantity || 1;
                const itemTotal = itemPrice * itemQuantity;

                return (
                  <div key={index} className="flex gap-4 pb-4 border-b border-neutral-100 last:border-0 last:pb-0">
                    <div className="w-20 h-24 bg-neutral-100 flex-shrink-0">
                      <img
                        src={
                          item.main_image?.startsWith('http')
                            ? item.main_image
                            : `https://pro-dash.orima.ma/${item.main_image}`
                        }
                        alt={item.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/150x200?text=No+Image';
                        }}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-normal text-neutral-900 mb-1 line-clamp-2">
                        {item.name}
                      </h4>
                      <p className="text-sm text-neutral-600 font-light">
                        Quantité: {itemQuantity}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-normal text-neutral-900">
                        {itemTotal.toLocaleString()} MAD
                      </p>
                      {itemQuantity > 1 && (
                        <p className="text-xs text-neutral-500 font-light mt-1">
                          {itemPrice.toLocaleString()} MAD × {itemQuantity}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Total */}
            <div className="mt-6 pt-6 border-t border-neutral-200">
              <div className="flex justify-between items-center">
                <span className="text-base font-normal text-neutral-900 uppercase tracking-wider">
                  Total
                </span>
                <span className="text-2xl font-normal text-neutral-900">
                  {total.toLocaleString()} MAD
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Info Box */}
        <div className="bg-neutral-100 border border-neutral-200 p-6 mb-6">
          <div className="flex items-start gap-3">
            <FaWhatsapp className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-neutral-900 mb-2">
                Nous vous contacterons via WhatsApp pour confirmer votre commande.
              </p>
              <p className="text-sm text-neutral-600 font-light">
                Notre équipe vous contactera bientôt pour confirmer la livraison.
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <button
            onClick={handleWhatsAppSupport}
            className="w-full bg-white text-neutral-900 border border-neutral-300 px-6 py-4 font-normal text-sm tracking-widest hover:bg-neutral-50 transition-all flex items-center justify-center gap-3 uppercase"
          >
            <FaWhatsapp className="w-5 h-5" />
            <span>Support WhatsApp</span>
          </button>
          
          <Link
            to="/shop"
            className="w-full bg-black text-white px-6 py-4 font-normal text-sm tracking-widest hover:bg-neutral-800 transition-all text-center uppercase"
          >
            Continuer mes achats
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ThankYou;
